import React, { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { BRANDS } from "@/data/brands";
import { BRANDS as BRAND_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";
import BrandHero from "@/pages/brand/BrandHero";
import AperinaHero from "@/pages/brand/AperinaHero";
import AperinaMoment from "@/pages/brand/AperinaMoment";
import WhiteElephantHero from "@/pages/brand/WhiteElephantHero";
import WhiteElephantMoments from "@/pages/brand/WhiteElephantMoments";
import WhiteElephantTriptych from "@/pages/brand/WhiteElephantTriptych";
import DesserHero from "@/pages/brand/DesserHero";
import DesserSignatures from "@/pages/brand/DesserSignatures";
import CafeoHero from "@/pages/brand/CafeoHero";
import CafeoMoment from "@/pages/brand/CafeoMoment";
import CafeoMoods from "@/pages/brand/CafeoMoods";
import MultimaltHero from "@/pages/brand/MultimaltHero";
import MultimaltElixirs from "@/pages/brand/MultimaltElixirs";
import MammHero from "@/pages/brand/MammHero";
import MammFamily from "@/pages/brand/MammFamily";
import BrandHighlights from "@/pages/brand/BrandHighlights";
import BrandLifestyle from "@/pages/brand/BrandLifestyle";
import BrandProductLine from "@/pages/brand/BrandProductLine";
import BrandDeck from "@/pages/brand/BrandDeck";
import BrandOthers from "@/pages/brand/BrandOthers";
import AgeGate from "@/components/site/AgeGate";

// Resolve translated content with a graceful fallback to the English brand
// data. Extracted so BrandPage stays a slim orchestrator. Plain function —
// not a hook — since it doesn't own any React state.
function resolveBrandContent(brand, t) {
  const trHighlights = t(`brandContent.${brand.slug}.highlights`);
  const highlights = Array.isArray(trHighlights) ? trHighlights : brand.highlights;
  const trVariants = t(`brandContent.${brand.slug}.variants`);
  const variants = Array.isArray(trVariants) ? trVariants : brand.variants;

  const trLifestyle = t(`brandContent.${brand.slug}.lifestyle`);
  const lifestyleT = trLifestyle && typeof trLifestyle === "object" ? trLifestyle : null;
  const lifestyle = {
    caption: lifestyleT?.caption || brand.lifestyle?.caption,
    title: lifestyleT?.title || brand.lifestyle?.title,
    body: lifestyleT?.body || brand.lifestyle?.body,
    benefits: Array.isArray(lifestyleT?.benefits)
      ? lifestyleT.benefits
      : brand.lifestyle?.benefits,
  };
  return { highlights, variants, lifestyle };
}

export default function BrandPage() {
  const { slug } = useParams();
  const { t } = useT();
  const brand = BRANDS.find((b) => b.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (!brand) return <Navigate to="/" replace />;

  const deck = brand.deckPages || [];
  const featured = deck[0];
  const gallery = deck.slice(1);
  const { highlights, variants, lifestyle } = resolveBrandContent(brand, t);

  // Alcohol brands must gate their content behind an age confirmation.
  const needsAgeGate = brand.slug === "white-elephant" || brand.slug === "aperina";
  const content = (
    <div className="App bg-white" data-testid={BRAND_IDS.page(brand.slug)}>
      <Header />

      <div className="h-2 w-full mt-20" style={{ background: brand.palette.accent }} />

      {brand.slug === "aperina" ? (
        <AperinaHero brand={brand} inquiryHref="/#b2b" />
      ) : brand.slug === "white-elephant" ? (
        <WhiteElephantHero brand={brand} inquiryHref="/#b2b" />
      ) : brand.slug === "desser" ? (
        <DesserHero brand={brand} inquiryHref="/#b2b" />
      ) : brand.slug === "cafeo" ? (
        <CafeoHero brand={brand} inquiryHref="/#b2b" />
      ) : brand.slug === "multimalt" ? (
        <MultimaltHero brand={brand} inquiryHref="/#b2b" />
      ) : brand.slug === "mamm" ? (
        <MammHero brand={brand} inquiryHref="/#b2b" />
      ) : (
        <BrandHero brand={brand} featured={featured} inquiryHref="/#b2b" />
      )}
      <BrandHighlights brand={brand} highlights={highlights} variants={variants} />

      {brand.slug === "aperina" && brand.artImage && (
        <AperinaMoment image={brand.artImage} />
      )}

      {brand.slug === "white-elephant" && (
        <>
          <WhiteElephantMoments />
          <WhiteElephantTriptych />
        </>
      )}

      {brand.slug === "desser" && <DesserSignatures />}

      {brand.slug === "cafeo" && (
        <>
          {brand.artImage && <CafeoMoment image={brand.artImage} />}
          <CafeoMoods />
        </>
      )}

      {brand.slug === "multimalt" && <MultimaltElixirs />}

      {brand.slug === "mamm" && <MammFamily />}

      {brand.lifestyle?.image && (
        <BrandLifestyle
          brand={brand}
          lifestyleCaption={lifestyle.caption}
          lifestyleTitle={lifestyle.title}
          lifestyleBody={lifestyle.body}
          lifestyleBenefits={lifestyle.benefits}
        />
      )}

      {brand.variantImages && brand.variantImages.length > 0 && (
        <BrandProductLine brand={brand} />
      )}

      {gallery.length > 0 && <BrandDeck brand={brand} gallery={gallery} />}

      <BrandOthers currentSlug={brand.slug} />

      <Footer />
    </div>
  );

  return needsAgeGate ? <AgeGate>{content}</AgeGate> : content;
}
